/**
 * The built-in bed (defs/bed.ts) as a data spec: free-placed furniture
 * (POINT anchor + FREE constraint), head at +n. The mattress follows the
 * footprint (a stretch group); everything else keeps real-world margins, so
 * it is formula-driven in `fixed` groups that read the live `width`/`depth`:
 *  - pillows 8 cm in from the head and sides, `pillowDepth` = 22 % of the
 *    depth but at most 45 cm;
 *  - `type` = double: two pillows side by side with 8 cm between them;
 *    single: one centered pillow, 70 % of the width but at most 80 cm;
 *  - the blanket line 10 cm below the pillows;
 *  - a turned-back fold at the foot corner, 25 % of the width but at most 35 cm.
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec } from '../spec';

const W = 1.6;
const D = 2.1;
const HW = W / 2;
const HD = D / 2;
/** Default-size values, for the authored drawing only (the formulas drive the live one). */
const MARGIN = 0.08;
const PILLOW_TOP = HD - MARGIN;
const PILLOW_BOTTOM = PILLOW_TOP - Math.min(0.45, D * 0.22);
const DOUBLE_PILLOW = (W - 3 * MARGIN) / 2;
const SINGLE_PILLOW = Math.min(0.8, W * 0.7);
const FOLD = Math.min(0.35, W * 0.25);

export const bedSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'POINT',
  constraint: { kind: 'FREE' },
  defaultSize: { width: W, depth: D, height: 0.5 },
  params: [
    {
      key: 'kind',
      kind: 'select',
      label: 'type',
      default: 'double',
      options: [
        { value: 'single', label: 'single' },
        { value: 'double', label: 'double' },
      ],
    },
  ],
  formulas: [
    { key: 'margin', label: 'Pillow margin', expr: `${MARGIN}`, note: 'Pillow inset from the mattress edges (m)' },
    { key: 'pillowTop', label: 'Pillow top edge', expr: 'depth / 2 - margin', note: 'Head-side edge of the pillows' },
    { key: 'pillowBottom', label: 'Pillow bottom edge', expr: 'pillowTop - min(0.45, depth * 0.22)', note: 'Foot-side edge of the pillows: 22 % of the depth, at most 45 cm' },
    { key: 'doublePillow', label: 'Double bed pillow width', expr: '(width - 3 * margin) / 2', note: 'Width of each pillow of a double bed (margins at both sides and between)' },
    { key: 'singlePillow', label: 'Single bed pillow width', expr: 'min(0.8, width * 0.7)', note: 'Width of the single pillow: 70 % of the width, at most 80 cm' },
    { key: 'blanket', label: 'Blanket line position', expr: 'pillowBottom - 0.1', note: 'Blanket line, 10 cm below the pillows' },
    { key: 'fold', label: 'Blanket fold size', expr: 'min(0.35, width * 0.25)', note: 'Size of the turned-back fold at the foot corner' },
  ],
  shapes: [
    { id: 'mattress', name: 'Mattress', kind: 'rect', min: [-HW, -HD], max: [HW, HD] },
    {
      id: 'pillowLeft',
      name: 'Left pillow',
      kind: 'rect',
      min: [-HW + MARGIN, PILLOW_BOTTOM],
      max: [-HW + MARGIN + DOUBLE_PILLOW, PILLOW_TOP],
      expr: { 'min.u': '-width / 2 + margin', 'min.n': 'pillowBottom', 'max.u': '-width / 2 + margin + doublePillow', 'max.n': 'pillowTop' },
    },
    {
      id: 'pillowRight',
      name: 'Right pillow',
      kind: 'rect',
      min: [HW - MARGIN - DOUBLE_PILLOW, PILLOW_BOTTOM],
      max: [HW - MARGIN, PILLOW_TOP],
      expr: { 'min.u': 'width / 2 - margin - doublePillow', 'min.n': 'pillowBottom', 'max.u': 'width / 2 - margin', 'max.n': 'pillowTop' },
    },
    {
      id: 'pillowSingle',
      name: 'Pillow',
      kind: 'rect',
      min: [-SINGLE_PILLOW / 2, PILLOW_BOTTOM],
      max: [SINGLE_PILLOW / 2, PILLOW_TOP],
      expr: { 'min.u': '-singlePillow / 2', 'min.n': 'pillowBottom', 'max.u': 'singlePillow / 2', 'max.n': 'pillowTop' },
    },
    {
      id: 'blanket',
      name: 'Blanket line',
      kind: 'polyline',
      points: [
        [-HW, PILLOW_BOTTOM - 0.1],
        [HW, PILLOW_BOTTOM - 0.1],
      ],
      closed: false,
      expr: { 'p0.u': '-width / 2', 'p0.n': 'blanket', 'p1.u': 'width / 2', 'p1.n': 'blanket' },
    },
    {
      id: 'fold',
      name: 'Blanket fold',
      kind: 'polyline',
      points: [
        [HW - FOLD, -HD],
        [HW, -HD + FOLD],
      ],
      closed: false,
      expr: { 'p0.u': 'width / 2 - fold', 'p0.n': '-depth / 2', 'p1.u': 'width / 2', 'p1.n': '-depth / 2 + fold' },
    },
  ],
  groups: [
    { id: 'mattress', name: 'Mattress', shapeIds: ['mattress'], resize: { u: 'stretch', n: 'stretch' } },
    { id: 'doublePillows', name: 'Double pillows', shapeIds: ['pillowLeft', 'pillowRight'], resize: { u: 'fixed', n: 'fixed' }, visibleWhen: { param: 'kind', equals: 'double' } },
    { id: 'singlePillow', name: 'Single pillow', shapeIds: ['pillowSingle'], resize: { u: 'fixed', n: 'fixed' }, visibleWhen: { param: 'kind', equals: 'single' } },
    { id: 'blanket', name: 'Blanket', shapeIds: ['blanket', 'fold'], resize: { u: 'fixed', n: 'fixed' } },
  ],
  model3d: null,
};
